import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { ArticleCard } from "@/components/ArticleCard";
import { KnotDivider } from "@/components/ornaments";
import { PageState } from "@/pages/PageState";
import { templateOf } from "@/lib/templates";
import { useT } from "@/lib/labels";

const LETTERS = ["All", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")];

const FacetFilters = ({ sectionSlug, values, onChange }) => {
  const [facets, setFacets] = useState(null);
  useEffect(() => { api.get(`/sections/${sectionSlug}/facets`).then((r) => setFacets(r.data)); }, [sectionSlug]);
  if (!facets) return null;
  const sel = (key, label, options) => (
    <label className="flex flex-col gap-1">
      <span className="admin-label !mb-0">{label}</span>
      <select data-testid={`filter-${key}`} value={values[key] || ""} onChange={(e) => onChange({ [key]: e.target.value })} className="admin-input !w-56">
        <option value="">All</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
  const active = values.kingdom || values.house || values.book;
  return (
    <div data-testid="character-filters" className="vellum p-4 mb-8 flex flex-wrap items-end gap-4">
      <SlidersHorizontal className="w-5 h-5 text-[color:var(--accent)] mb-2.5" />
      {sel("kingdom", "Kingdom", facets.kingdom.map((k) => ({ value: k, label: k })))}
      {sel("house", "House", facets.house.map((h) => ({ value: h, label: h })))}
      {sel("book", "Appears in book", facets.books.map((b) => ({ value: b.id, label: `Book ${b.number}: ${b.title}` })))}
      {active && <button data-testid="filter-clear" onClick={() => onChange({ kingdom: "", house: "", book: "" })} className="font-heading text-xs underline mb-3">Clear filters</button>}
    </div>
  );
};

export const ArticleBrowser = ({ sectionSlug, showSection, filters }) => {
  const { sections } = useSite();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const letter = params.get("letter") || "";
  const page = Number(params.get("page") || 1);
  const facet = { kingdom: params.get("kingdom") || "", house: params.get("house") || "", book: params.get("book") || "" };
  const [data, setData] = useState(null);
  const [term, setTerm] = useState(q);
  const secName = Object.fromEntries(sections.map((s) => [s.id, s.name]));

  useEffect(() => { setTerm(q); }, [q]);
  useEffect(() => {
    api.get("/articles", { params: { section: sectionSlug, q: q || undefined, letter: letter || undefined, page, limit: 24,
      kingdom: facet.kingdom || undefined, house: facet.house || undefined, book: facet.book || undefined } }).then((r) => setData(r.data));
  }, [sectionSlug, q, letter, page, facet.kingdom, facet.house, facet.book]);

  const update = (patch) => {
    const next = Object.fromEntries(Object.entries({ q, letter, ...facet, page: 1, ...patch }).filter(([, v]) => v && v !== 1 && v !== "All"));
    setParams(next);
  };

  return (
    <div>
      {filters && <FacetFilters sectionSlug={sectionSlug} values={facet} onChange={update} />}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-8">
        <form onSubmit={(e) => { e.preventDefault(); update({ q: term }); }} className="flex items-center gap-2 border border-[#c9a227] bg-[#fbf7ec]/80 px-3 py-2 lg:w-80">
          <Search className="w-4 h-4" />
          <input data-testid="browser-search-input" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search these pages…" className="bg-transparent outline-none w-full" />
        </form>
        <div data-testid="browser-letter-bar" className="flex flex-wrap gap-1">
          {LETTERS.map((l) => (
            <button key={l} data-testid={`letter-${l}`} onClick={() => update({ letter: l })}
              className={`font-heading text-xs w-8 h-8 border transition-colors ${(letter || "All") === l ? "bg-[#991b1b] text-[#fdf6e3] border-[#c9a227]" : "border-[#c9a227]/40 hover:bg-[#c9a227]/20"} ${l === "All" ? "w-12" : ""}`}>{l}</button>
          ))}
        </div>
      </div>
      {!data ? <p className="italic">Unrolling…</p> : data.items.length === 0 ? (
        <p data-testid="browser-empty" className="italic text-lg text-[#5e4a35] py-12">No pages have been inscribed here yet.</p>
      ) : (
        <div data-testid="article-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {data.items.map((a, i) => <ArticleCard key={a.id} a={a} index={i} sectionName={showSection ? secName[a.section_id] : undefined} />)}
        </div>
      )}
      {data && data.pages > 1 && (
        <div className="flex justify-center gap-2 mt-12">
          {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
            <button key={p} data-testid={`page-${p}`} onClick={() => update({ page: p })} className={`font-heading w-10 h-10 border border-[#c9a227] ${p === page ? "bg-[#1e4976] text-white" : "hover:bg-[#c9a227]/20"}`}>{p}</button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function SectionPage() {
  const { slug } = useParams();
  const [s, setS] = useState(undefined);
  const t = useT();
  useEffect(() => { setS(undefined); api.get(`/sections/${slug}`).then((r) => setS(r.data)).catch(() => setS(null)); }, [slug]);
  if (!s) return <PageState loading={s === undefined} />;
  const Icon = templateOf(s.template).icon;
  return (
    <div data-testid="section-page" className={`max-w-7xl mx-auto px-6 sm:px-8 py-12 theme-${s.theme}`}>
      <nav className="text-sm font-heading text-[#5e4a35] mb-8"><Link to="/" className="hover:text-[#991b1b]">Home</Link> <span className="mx-2">›</span> {s.name}</nav>
      <header className="flex items-start gap-6 rise">
        {s.icon_image ? <img src={imgUrl(s.icon_image)} alt="" className="w-20 h-20 object-cover border-2 border-[#c9a227]" />
          : <span className="w-20 h-20 grid place-items-center border-2 border-[#c9a227] bg-[color:var(--accent)] text-[#fdf6e3] shrink-0"><Icon className="w-9 h-9" /></span>}
        <div>
          {t("section.eyebrow") && <p className="eyebrow">{t("section.eyebrow")}</p>}
          <h1 data-testid="section-title" className="font-display text-4xl sm:text-5xl mt-1">{s.name}</h1>
          <p className="mt-3 text-lg italic text-[#4a3a2a] max-w-2xl">{s.description}</p>
        </div>
      </header>
      <KnotDivider className="my-10" />
      <ArticleBrowser sectionSlug={s.slug} filters={templateOf(s.template).layout === "character"} />
    </div>
  );
}
